import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiService } from '../../../services/api';
import { CreateContratoData } from '../../../types/contratos';
import OpportunityForm from '../deals/OpportunityForm';
import { Funnel } from '../../../types/funnels';
import { CreateOpportunityData } from '../../../types/opportunity';

interface OpportunityOption {
    value: number;
    label: string;
    id_contato?: number;
    titulo?: string;
}

const AddContratoForm = () => {
    // Estado para controlar o passo do formulário
    const [step, setStep] = useState<number>(1);

    // Estado para controlar a opção de fluxo (vincular ou criar)
    const [flowOption, setFlowOption] = useState<'vincular' | 'criar' | null>(null);

    // Estado para armazenar as oportunidades
    const [opportunities, setOpportunities] = useState<OpportunityOption[]>([]);
    const [isLoadingOpportunities, setIsLoadingOpportunities] = useState<boolean>(true);
    const [errorOpportunities, setErrorOpportunities] = useState<string | null>(null);

    // Estado para a oportunidade selecionada ou criada
    const [selectedOpportunity, setSelectedOpportunity] = useState<OpportunityOption | null>(null);
    const [createdOpportunityId, setCreatedOpportunityId] = useState<number | null>(null);

    // Estados para o formulário de contrato
    const [dataInicio, setDataInicio] = useState<Date | null>(new Date());
    const [dataFinalizacao, setDataFinalizacao] = useState<Date | null>(null);
    const [descricao, setDescricao] = useState<string>('');
    const [valorTotal, setValorTotal] = useState<string>('');
    const [situacao, setSituacao] = useState<string>('Aguardando Início');
    const [contatoId, setContatoId] = useState<number | null>(null);
    const [compraDirecta, setCompraDirecta] = useState<boolean>(false);

    // Estados para OpportunityForm
    const [funnels, setFunnels] = useState<Funnel[]>([]);
    const [selectedFunnel, setSelectedFunnel] = useState<Funnel | null>(null);
    const [usersList, setUsersList] = useState<any[]>([]);
    const [openModal, setOpenModal] = useState<boolean>(false);

    // Estados para controle de submissão
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Buscar oportunidades ao carregar o componente
    useEffect(() => {
        const fetchOpportunities = async () => {
            try {
                setIsLoadingOpportunities(true);
                setErrorOpportunities(null);

                // Buscar todas as oportunidades
                const response = await apiService.getOpportunitiesByStage(0, 0);

                if (response.success && response.data) {
                    const options = response.data.map(opp => ({
                        value: opp.id,
                        label: `${opp.titulo} - ${opp.valor_licitacao ? new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                        }).format(parseFloat(opp.valor_licitacao)) : 'Valor não definido'}`,
                        id_contato: opp.id_contato,
                        titulo: opp.titulo
                    }));

                    setOpportunities(options);
                } else {
                    setErrorOpportunities('Não foi possível carregar as oportunidades');
                }
            } catch (error) {
                console.error('Erro ao buscar oportunidades:', error);
                setErrorOpportunities('Erro ao carregar oportunidades');
            } finally {
                setIsLoadingOpportunities(false);
            }
        };

        // Buscar funis para o formulário de oportunidade
        const fetchFunnels = async () => {
            try {
                const response = await apiService.getFunis();
                if (response.success && response.data) {
                    setFunnels(response.data);
                    if (response.data.length > 0) {
                        setSelectedFunnel(response.data[0]);
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar funis:', error);
            }
        };

        // Criar lista de usuários
        const getCurrentUser = () => {
            const userInfo = localStorage.getItem('user');
            if (userInfo) {
                const userData = JSON.parse(userInfo);
                setUsersList([{
                    value: userData.id,
                    label: userData.nome || userData.name
                }]);
            }
        };

        fetchOpportunities();
        fetchFunnels();
        getCurrentUser();
    }, []);

    const handleOpportunityChange = (selected: OpportunityOption | null) => {
        setSelectedOpportunity(selected);
        if (selected) {
            // Atualizar o ID do contato
            setContatoId(selected.id_contato || null);

            // Preencher a descrição com o título da oportunidade
            if (selected.titulo) {
                setDescricao(selected.titulo);
            }

            // Definir compra direta como false quando vincular a uma oportunidade existente
            setCompraDirecta(false);
        }
    };

    const handleFlowOptionSelect = (option: 'vincular' | 'criar') => {
        setFlowOption(option);
        setStep(2);
        setErrorMessage(null);

        // Definir o valor padrão de compra direta com base na opção
        setCompraDirecta(option === 'criar');
    };

    const handleNextStep = () => {
        if (flowOption === 'vincular' && !selectedOpportunity) {
            setErrorMessage('Por favor, selecione uma oportunidade para continuar');
            return;
        }

        setErrorMessage(null);
        setStep(3);
    };

    const handlePreviousStep = () => {
        if (step === 2) {
            setStep(1);
            setFlowOption(null);
        } else if (step === 3) {
            setStep(2);
        }
        setErrorMessage(null);
    };

    const handleCurrencyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numericValue = value.replace(/\D/g, '');
        const formattedValue = new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(Number(numericValue) / 100);

        setValorTotal(formattedValue);
    };

    // Handler para o formulário de oportunidade
    const handleOpportunityCreated = useCallback((createdOpportunity: any) => {
        console.log("Oportunidade criada:", createdOpportunity);

        // Armazenar o ID da oportunidade criada
        const opportunityId = parseInt(createdOpportunity.id);
        setCreatedOpportunityId(opportunityId);

        // Verificar todas as possíveis fontes do ID do contato
        const contactId = createdOpportunity.id_contato ||
            (typeof createdOpportunity.contact === 'object' && createdOpportunity.contact.value) ||
            undefined;  // Alterado de null para undefined para corresponder à tipagem

        console.log("ID do contato a ser usado como licitante:", contactId);

        // Adicionar a oportunidade criada como selecionada com a tipagem correta
        const newOption: OpportunityOption = {
            value: opportunityId,
            label: `${createdOpportunity.title} - ${createdOpportunity.value}`,
            id_contato: contactId,  // Agora é number | undefined, compatível com a interface
            titulo: createdOpportunity.title
        };

        setSelectedOpportunity(newOption);

        // Preencher a descrição com o título da oportunidade
        setDescricao(createdOpportunity.title || '');

        // Definir o ID do contato para o estado contatoId
        setContatoId(contactId || null);  // Aqui podemos usar null pois o estado contatoId é number | null

        // Definir compra direta como true quando criar uma nova oportunidade
        setCompraDirecta(true);

        // Avançar diretamente para a etapa de contrato
        setStep(3);
    }, []);

    const handleOpportunityFormSubmit = async (data: CreateOpportunityData) => {
        try {
            setIsSubmitting(true);
            console.log("Dados da oportunidade a serem enviados:", data);

            const response = await apiService.createOpportunity(data);

            if (response.success && response.data) {
                console.log("Resposta da criação de oportunidade:", response.data);

                handleOpportunityCreated({
                    id: response.data.id,
                    title: data.titulo,
                    value: new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                    }).format(data.valor_licitacao),
                    id_contato: data.id_contato
                });
            } else {
                setErrorMessage(response.message || 'Erro ao criar oportunidade');
            }
        } catch (error: any) {
            console.error("Erro na criação da oportunidade:", error);
            setErrorMessage(error.message || 'Erro ao criar oportunidade');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Verificar campos obrigatórios
        if (!dataInicio || !valorTotal) {
            setErrorMessage('Por favor, preencha todos os campos obrigatórios do contrato');
            return;
        }

        // Obter ID da oportunidade
        const opportunityId = createdOpportunityId || (selectedOpportunity ? selectedOpportunity.value : null);

        if (!opportunityId) {
            setErrorMessage('Não foi possível identificar a oportunidade');
            return;
        }

        try {
            setIsSubmitting(true);
            setErrorMessage(null);

            // Extrair valor numérico do formato de moeda
            const valorNumerico = parseFloat(
                valorTotal.replace(/[^\d,]/g, '').replace(',', '.')
            );

            // Determinar o valor de compra_direta com base no fluxo
            const isCompraDirecta = compraDirecta;

            console.log("Dados do contrato a serem enviados:", {
                id_oportunidade: opportunityId,
                id_licitante: contatoId, // Usando o ID do contato como ID do licitante
                compra_direta: isCompraDirecta, // Novo campo
                data_inicio: dataInicio ? dataInicio.toISOString().split('T')[0] : '',
                valor_total: valorNumerico,
                outros: {
                    dataFinalizacao,
                    descricao,
                    situacao
                }
            });

            // Preparar dados para API
            const contratoData: CreateContratoData = {
                id_oportunidade: opportunityId,
                id_licitante: contatoId || 0, // Usando o ID do contato como ID do licitante
                compra_direta: isCompraDirecta, // Novo campo
                data_inicio: dataInicio.toISOString().split('T')[0], // Formato YYYY-MM-DD
                valor_total: valorNumerico,
                situacao: situacao
            };

            // Adicionar campos opcionais se preenchidos
            if (dataFinalizacao) {
                contratoData.data_finalizacao = dataFinalizacao.toISOString().split('T')[0];
            }

            if (descricao) {
                contratoData.descricao = descricao;
            }

            // Enviar para API
            const response = await apiService.createContrato(contratoData);

            if (response.success) {
                setSuccessMessage('Contrato criado com sucesso!');

                // Limpar formulário
                setSelectedOpportunity(null);
                setDataInicio(new Date());
                setDataFinalizacao(null);
                setDescricao('');
                setValorTotal('');
                setSituacao('Aguardando Início');
                setCreatedOpportunityId(null);
                setFlowOption(null);
                setContatoId(null);
                setCompraDirecta(false); // Resetar o campo compra_direta

                // Voltar ao primeiro passo
                setStep(1);

                // Fechar modal após sucesso
                setTimeout(() => {
                    const closeButton = document.querySelector('[data-bs-dismiss="offcanvas"]');
                    if (closeButton) {
                        (closeButton as HTMLElement).click();
                    }

                    // Recarregar a página para atualizar a lista de contratos
                    window.location.reload();
                }, 2000);
            } else {
                setErrorMessage(response.message || 'Erro ao criar contrato');
            }
        } catch (error: any) {
            console.error('Erro ao criar contrato:', error);
            setErrorMessage(error.message || 'Ocorreu um erro ao criar o contrato');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Opções para o select de situação
    const situacaoOptions = [
        { value: 'Aguardando Início', label: 'Aguardando Início' },
        { value: 'Em andamento', label: 'Em andamento' },
        { value: 'Finalizado', label: 'Finalizado' },
        { value: 'Vencido', label: 'Vencido' },
        { value: 'A Vencer', label: 'A Vencer' },
        { value: 'Finalizado com Renovação', label: 'Finalizado com Renovação' }
    ];

    // Renderizar título baseado na etapa
    const renderTitle = () => {
        if (step === 1) return 'Adicionar Contrato';
        if (step === 2) {
            if (flowOption === 'vincular') return 'Selecionar Oportunidade';
            if (flowOption === 'criar') return 'Criar Nova Oportunidade';
        }
        if (step === 3) return 'Detalhes do Contrato';
        return 'Adicionar Contrato';
    };

    return (
        <>
            <Link
                to="#"
                className="btn btn-primary"
                data-bs-toggle="offcanvas"
                data-bs-target="#offcanvas_add_contrato"
            >
                <i className="ti ti-plus" /> Adicionar Contrato
            </Link>

            {/* Offcanvas para adicionar contrato */}
            <div
                className="offcanvas offcanvas-end offcanvas-large"
                tabIndex={-1}
                id="offcanvas_add_contrato"
            >
                <div className="offcanvas-header border-bottom">
                    <h4>{renderTitle()}</h4>
                    <button
                        type="button"
                        className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                        data-bs-dismiss="offcanvas"
                        aria-label="Close"
                    >
                        <i className="ti ti-x" />
                    </button>
                </div>
                <div className="offcanvas-body">
                    {successMessage && (
                        <div className="alert alert-success alert-dismissible fade show" role="alert">
                            {successMessage}
                            <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
                        </div>
                    )}

                    {errorMessage && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            {errorMessage}
                            <button type="button" className="btn-close" onClick={() => setErrorMessage(null)}></button>
                        </div>
                    )}

                    {/* Etapa 1: Escolher entre vincular ou criar oportunidade */}
                    {step === 1 && (
                        <div>
                            <div className="text-center mb-4">
                                <p>Para criar um contrato, você precisa associá-lo a uma oportunidade.</p>
                                <p>Escolha uma das opções abaixo:</p>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div
                                        className="card cursor-pointer text-center p-4 mb-3 shadow-sm hover-shadow h-100 border-hover-primary"
                                        onClick={() => handleFlowOptionSelect('vincular')}
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            borderLeft: '4px solid #e0e0e0'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-5px)';
                                            e.currentTarget.style.borderLeftColor = '#4361ee';
                                            e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.borderLeftColor = '#e0e0e0';
                                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
                                        }}
                                    >
                                        <div className="mb-3">
                                            <div className="icon-circle bg-light-primary">
                                                <i className="ti-link text-primary" style={{ fontSize: '36px' }}></i>
                                            </div>
                                        </div>
                                        <h5 className="card-title">Vincular a Oportunidade Existente</h5>
                                        <p className="text-muted">Selecione uma oportunidade já cadastrada no sistema.</p>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div
                                        className="card cursor-pointer text-center p-4 mb-3 shadow-sm hover-shadow h-100 border-hover-success"
                                        onClick={() => handleFlowOptionSelect('criar')}
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            borderLeft: '4px solid #e0e0e0'
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-5px)';
                                            e.currentTarget.style.borderLeftColor = '#2e7d32';
                                            e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.borderLeftColor = '#e0e0e0';
                                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)';
                                        }}
                                    >
                                        <div className="mb-3">
                                            <div className="icon-circle">
                                                <i className="ti-plus text-success" style={{ fontSize: '36px' }}></i>
                                            </div>
                                        </div>
                                        <h5 className="card-title">Criar Nova Oportunidade</h5>
                                        <p className="text-muted">Cadastre uma nova oportunidade e vincule-a ao contrato.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                                <button
                                    type="button"
                                    data-bs-dismiss="offcanvas"
                                    className="btn btn-light"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Etapa 2: Selecionar ou criar oportunidade */}
                    {step === 2 && (
                        <>
                            {/* Opção: Vincular a oportunidade existente */}
                            {flowOption === 'vincular' && (
                                <div>
                                    <div className="mb-4">
                                        <label className="col-form-label">
                                            Selecione uma oportunidade <span className="text-danger">*</span>
                                        </label>
                                        {isLoadingOpportunities ? (
                                            <div className="text-center p-3">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Carregando...</span>
                                                </div>
                                                <span className="ms-2">Carregando oportunidades...</span>
                                            </div>
                                        ) : errorOpportunities ? (
                                            <div className="alert alert-danger py-2">
                                                {errorOpportunities}
                                            </div>
                                        ) : (
                                            <Select
                                                options={opportunities}
                                                value={selectedOpportunity}
                                                onChange={handleOpportunityChange}
                                                placeholder="Selecione uma oportunidade"
                                                classNamePrefix="react-select"
                                                isSearchable
                                                noOptionsMessage={() => "Nenhuma oportunidade encontrada"}
                                            />
                                        )}
                                    </div>

                                    <div className="d-flex align-items-center justify-content-between mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={handlePreviousStep}
                                        >
                                            Voltar
                                        </button>
                                        <div>
                                            <button
                                                type="button"
                                                data-bs-dismiss="offcanvas"
                                                className="btn btn-light me-2"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleNextStep}
                                                disabled={!selectedOpportunity || isLoadingOpportunities}
                                            >
                                                Próximo
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Opção: Criar nova oportunidade */}
                            {flowOption === 'criar' && (
                                <div>
                                    {/* Usar o componente de formulário de oportunidade existente */}
                                    <OpportunityForm
                                        funnels={funnels}
                                        selectedFunnel={selectedFunnel}
                                        setSelectedFunnel={setSelectedFunnel}
                                        usersList={usersList}
                                        setOpenModal={setOpenModal}
                                        onSubmit={handleOpportunityFormSubmit}
                                        onOpportunityCreated={handleOpportunityCreated}
                                        onClose={handlePreviousStep}
                                        modalType="create"
                                        closeOnSuccess={false}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {/* Etapa 3: Formulário de contrato */}
                    {step === 3 && (
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-12 mb-3">
                                    <div className="alert alert-info">
                                        <strong>Oportunidade selecionada:</strong> {selectedOpportunity?.label}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="col-form-label">
                                            Data de Início <span className="text-danger">*</span>
                                        </label>
                                        <div className="icon-form">
                                            <span className="form-icon">
                                                <i className="ti ti-calendar-check" />
                                            </span>
                                            <DatePicker
                                                className="form-control datetimepicker"
                                                selected={dataInicio}
                                                onChange={(date) => setDataInicio(date)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="DD/MM/AAAA"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="col-form-label">
                                            Data de Finalização
                                        </label>
                                        <div className="icon-form">
                                            <span className="form-icon">
                                                <i className="ti ti-calendar-check" />
                                            </span>
                                            <DatePicker
                                                className="form-control datetimepicker"
                                                selected={dataFinalizacao}
                                                onChange={(date) => setDataFinalizacao(date)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="DD/MM/AAAA"
                                                isClearable
                                                minDate={dataInicio}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <div className="mb-3">
                                        <label className="col-form-label">
                                            Descrição
                                        </label>
                                        <textarea
                                            className="form-control"
                                            value={descricao}
                                            onChange={(e) => setDescricao(e.target.value)}
                                            rows={3}
                                            placeholder="Descreva o contrato..."
                                        />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="col-form-label">
                                            Valor Total <span className="text-danger">*</span>
                                        </label>
                                        <div className="icon-form">
                                            <span className="form-icon">
                                                <i className="ti ti-cash" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={valorTotal}
                                                onChange={handleCurrencyInput}
                                                placeholder="R$ 0,00"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="col-form-label">
                                            Situação
                                        </label>
                                        <Select
                                            options={situacaoOptions}
                                            value={situacaoOptions.find(option => option.value === situacao)}
                                            onChange={(selected) => selected && setSituacao(selected.value)}
                                            placeholder="Selecione a situação"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <div className="mb-3 form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="compraDireta"
                                            checked={compraDirecta}
                                            onChange={(e) => setCompraDirecta(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="compraDireta">
                                            Compra Direta
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handlePreviousStep}
                                >
                                    Voltar
                                </button>
                                <div>
                                    <button
                                        type="button"
                                        data-bs-dismiss="offcanvas"
                                        className="btn btn-light me-2"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Criando...
                                            </>
                                        ) : (
                                            'Criar Contrato'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

export default AddContratoForm;